// src/lib/http/session.ts
// The browser's view of its identity session (contract §6.2, §6.4): built from GET /auth/session and the login/refresh bodies, never from a cookie.

import { useSyncExternalStore } from "react";
import { fetchAuthSession } from "../../api/auth/authApi";
import type { LegacyUserId, UserUuid } from "../../types/identity";

export interface SessionTimes {
	sid: string;
	audience: "staff" | "patient";
	access_expires_at: string;
	idle_expires_at: string;
	absolute_expires_at: string;
}

export interface MfaStatus {
	required: boolean;
	state: "satisfied" | "not_required" | "grace" | "enrolment_required";
	enrolled: boolean;
	methods: string[];
	grace_until: string | null;
	password_login_enabled: boolean;
}

export interface SessionUserRef {
	uuid: UserUuid;
	legacy_id: LegacyUserId | null;
	username: string;
	email_verified?: boolean;
}

// GET /auth/session; the login `authenticated`/`recovery` body is the same minus apps, rbac_ver and auth_time.
export interface AuthSessionInfo {
	user: SessionUserRef;
	roles: string[];
	permissions: string[];
	apps?: string[];
	rbac_ver?: number;
	amr: string[];
	auth_time?: string;
	session: SessionTimes;
	mfa: MfaStatus;
}

export interface LoginAuthenticated extends AuthSessionInfo {
	status: "authenticated" | "recovery";
}

export interface LoginMfaRequired {
	status: "mfa_required";
	challenge_id: string;
	methods: string[];
	recovery_methods: string[];
	expires_at: string;
}

export type LoginResponse = LoginAuthenticated | LoginMfaRequired;

export interface RefreshResponse {
	status: "refreshed";
	session: SessionTimes;
	mfa: MfaStatus;
}

// unknown: not asked yet; error: identity could not answer (not a sign-out).
export type AuthStatus = "unknown" | "authenticated" | "anonymous" | "error";

export interface AuthSessionState {
	status: AuthStatus;
	info: AuthSessionInfo | null;
	// Why the last session ended (an error code from §11, or "logged_out"); null while one is live.
	endedReason: string | null;
}

let state: AuthSessionState = { status: "unknown", info: null, endedReason: null };
const listeners = new Set<(next: AuthSessionState, previous: AuthSessionState) => void>();

let timesReceivedAt = 0;

const publish = (next: AuthSessionState): void => {
	const previous = state;
	state = next;
	if (next.info?.session !== previous.info?.session) timesReceivedAt = Date.now();
	listeners.forEach((listener) => listener(next, previous));
};

export const getAuthSession = (): AuthSessionState => state;

// When this tab last took session times from identity, on its own clock.
export const sessionTimesReceivedAt = (): number => timesReceivedAt;

export const isSignedIn = (): boolean => state.status === "authenticated";

export const setAuthenticated = (info: AuthSessionInfo): void => {
	publish({ status: "authenticated", info: { ...info, roles: info.roles ?? [], permissions: info.permissions ?? [], amr: info.amr ?? [] }, endedReason: null });
};

// A refresh rotates the session times and recomputes MFA; identity, roles and permissions stay as last read.
export const applyRefresh = (body: Pick<RefreshResponse, "session"> & Partial<Pick<RefreshResponse, "mfa">>): void => {
	if (!state.info) return;
	publish({ ...state, info: { ...state.info, session: body.session ?? state.info.session, mfa: body.mfa ?? state.info.mfa } });
};

// A step-up re-issues the access cookie with a fresh auth_time and amr; the session itself is unchanged (contract §6.7).
export const applyStepUp = (body: { amr: string[]; auth_time: string; session: SessionTimes }): void => {
	if (!state.info) return;
	publish({ ...state, info: { ...state.info, amr: body.amr ?? state.info.amr, auth_time: body.auth_time, session: body.session ?? state.info.session } });
};

// Confined to enrolment (contract §4.7): every /api route answers 403, so the app waits on the security page instead of /me.
export const isEnrolmentConfined = (info: AuthSessionInfo | null | undefined = state.info): boolean => info?.mfa?.state === "enrolment_required";

// Signed in and not confined: the only state in which the app's own services (and their sockets) will answer.
export const isAppSession = (current: AuthSessionState = state): boolean => current.status === "authenticated" && !isEnrolmentConfined(current.info);

// Signed in through a recovery code, SMS or email: it must enrol a new factor, never step up with the lost one (S32).
export const isRecoverySession = (info: AuthSessionInfo | null | undefined = state.info): boolean => info?.amr?.includes("recovery") ?? false;

// Only otp or hwk in amr satisfies a step-up or lifts confinement (contract §6.7).
export const hasStrongAmr = (info: AuthSessionInfo | null | undefined = state.info): boolean =>
	Boolean(info?.amr?.includes("otp") || info?.amr?.includes("hwk"));

export const setAnonymous =(endedReason: string | null = null): void => {
	if (state.status === "anonymous" && state.info === null && state.endedReason === endedReason) return;
	publish({ status: "anonymous", info: null, endedReason });
};

export const setSessionError = (): void => {
	publish({ status: "error", info: state.info, endedReason: null });
};

export const resetAuthSession = (): void => {
	publish({ status: "unknown", info: null, endedReason: null });
};

export const subscribeAuthSession = (listener: (next: AuthSessionState, previous: AuthSessionState) => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const useAuthSession = (): AuthSessionState =>
	useSyncExternalStore(
		(onChange) => subscribeAuthSession(onChange),
		() => state,
		() => state
	);

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

let loading: Promise<AuthSessionInfo | null> | null = null;
let lastValidatedAt = 0;
const REVALIDATE_AFTER_MS = 60_000;

// Single-flight GET /auth/session. A 401 (after the client's one refresh) is "signed out"; anything else is "identity unavailable".
export const loadAuthSession = (): Promise<AuthSessionInfo | null> => {
	if (!loading) {
		loading = fetchAuthSession()
			.then((info) => {
				lastValidatedAt = Date.now();
				setAuthenticated(info);
				return info;
			})
			.catch((error: unknown) => {
				const status = statusOf(error);
				if (status === 401 || status === 403) {
					setAnonymous(state.endedReason);
					return null;
				}
				console.error("session.load.failed", { status });
				// A failed background re-check keeps a live session; only an unresolved one becomes "unavailable".
				if (state.status !== "authenticated") setSessionError();
				throw error;
			})
			.finally(() => {
				loading = null;
			});
	}
	return loading;
};

// At most once a minute: the cookies are shared, so a sign-in as someone else in another tab is visible here.
export const revalidateAuthSession = async (): Promise<void> => {
	if (state.status !== "authenticated" || Date.now() - lastValidatedAt < REVALIDATE_AFTER_MS) return;
	lastValidatedAt = Date.now();
	try {
		await loadAuthSession();
	} catch {
		// Logged in loadAuthSession; a transient failure must not sign anyone out.
	}
};

// UI gating by permission name (contract §6.4: `permissions` is every permission the user holds).
export const can = (permission: string): boolean => state.info?.permissions.includes(permission) ?? false;
